import PropTypes from 'prop-types'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { ROLLOUT_ON } from './bucketing'
import { buildContext, getFlags, resolveFlag, setFlags } from './flags-store'
import { trackExposure } from './track-exposure'
import { useFeatureFlagsQuery } from './use-feature-flags-query'

const FlagsContext = createContext()

FlagsContext.displayName = 'FlagsContext'

/**
 * Laedt FEATURE_FLAGS und haelt sie fuer useVariant/useFlag bereit. Liegt in
 * App.js ueber allen Routen, auch ueber der public Route /funnel. Blockiert
 * nie: ohne Flags rendert alles im Status quo.
 */
export function FlagsProvider({ children }) {
  const { data, isFetched } = useFeatureFlagsQuery()

  // Synchron im Render, damit Kinder im selben Render die Flags sehen. Nach
  // Restore aus dem persistierten Cache laeuft die queryFn nicht, deshalb hier.
  if (data) setFlags(data)

  const value = useMemo(
    () => ({ flags: data ? getFlags() : {}, isLoaded: isFetched || !!data }),
    [data, isFetched]
  )

  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>
}

FlagsProvider.propTypes = {
  children: PropTypes.node.isRequired
}

const useFlagsContext = () => {
  const context = useContext(FlagsContext)
  if (context === undefined) {
    throw new Error('useVariant/useFlag must be used within a FlagsProvider')
  }
  return context
}

/**
 * Variante dieses Nutzers bzw. Besuchers. null, solange das Flag unbekannt
 * oder im Entwurf ist, sonst mindestens Control. Schreibt die Exposure selbst,
 * wenn die Zuteilung fuer die Auswertung zaehlt.
 *
 * @param {string} key
 * @param {object} [options]
 * @param {string} [options.formId] verknuepft die Exposure mit dem Lead
 * @param {boolean} [options.track=true] false, wenn die Variante nur gelesen
 *   und noch nicht gezeigt wird
 * @param {object} [options.ctx] Felder fuer buildContext, z. B. `{ partnerId }`.
 *   Noetig fuer Flags mit unit partner im ersten Render, das Partner-Singleton
 *   ist dann noch leer. Gesetzte Felder gewinnen gegen die Singletons.
 */
export const useVariant = (
  key,
  { formId, track = true, ctx: ctxOverrides } = {}
) => {
  const { flags, isLoaded } = useFlagsContext()
  const flag = flags[key]

  const ctx = buildContext(ctxOverrides)
  const { variant, tracked } = resolveFlag(key, ctx)

  useEffect(() => {
    if (!isLoaded || !track || !tracked || !flag) return
    trackExposure({ flag, variant, ctx, formId })
    // ctx bewusst nicht in den Deps: neues Objekt pro Render, die relevanten
    // IDs stecken in variant und werden pro Unit dedupliziert
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, track, tracked, flag, variant, formId])

  return variant
}

/**
 * Rollout an? Fuer Experimente true bei jeder Variante ausser Control.
 * Nimmt dieselben Optionen wie useVariant, also auch `ctx`.
 */
export const useFlag = (key, options) => {
  const { flags } = useFlagsContext()
  const variant = useVariant(key, options)
  const flag = flags[key]

  if (!flag || !variant) return false
  if (flag.kind === 'rollout') return variant === ROLLOUT_ON

  return variant !== (flag.variants?.[0]?.key ?? null)
}

export const useFlagsLoaded = () => useFlagsContext().isLoaded

/**
 * true, sobald die Flags da sind oder `timeoutMs` vorbei ist. Fuer Oberflaechen,
 * die nicht zwischen Varianten springen duerfen (Funnel): kurz warten, dann
 * mit dem rendern, was da ist. Einmal true, bleibt es true.
 */
export const useFlagsReady = (timeoutMs = 1500) => {
  const isLoaded = useFlagsLoaded()
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    if (isLoaded) return undefined
    const timer = setTimeout(() => setTimedOut(true), timeoutMs)
    return () => clearTimeout(timer)
  }, [isLoaded, timeoutMs])

  return isLoaded || timedOut
}
